import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CellHoverContext,
  useChipPresent,
  useEnteredCurrency,
} from 'components/CellAnnotationValueViewer/components/MoneyChipValueViewer';
import { usePatchCapTableSecurities } from 'services/hooks/captable';
import { useReviewDrawerOpen, useReviewerMode, useReviewItemStatus } from 'store/useReviewStore';
import CitationValueViewerProps from './CitationValueViewer.props';
import {
  citationBadgeSx,
  StyledCitationNumber,
  StyledCitationNumberWrapper,
  StyledCitationValueViewer,
  StyledCitationValueViewerWrapper,
} from './CitationValueViewer.styles';
import { hasInlineControl } from './inlineControlLayout';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { EventType, useEventBus } from '../../../../../context';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import {
  citationReviewStatus,
  isAiCreatedCitation,
  pickActiveCitation,
  reviewStatusOf,
  statusToHighlightVariant,
} from '../../utilities/reviewAggregator';
import { removeCitationFromSecurities } from '../../utilities/securityAiPatches';
import { reviewPendingTextSx } from '../ai-security-finding-popover/AIMergeValueViewer.styles';
import { useCitationContext } from '../captable-table/CitationContext';

const CitationNestingContext = React.createContext(false);

// Footnote-badge gutter geometry, all in the overlay wrapper's LOCAL (unscaled)
// px space. The badge default-anchors 2px from the cell's left edge; on a
// chip-bearing cell it moves just past the chip's measured right edge plus a
// small gap (matching the chip row's own 4px item gap). When the chip exists but
// cannot be measured yet (first paint, jsdom) a fixed reserve stands in:
// wrapper padding (4) + widest expected 3-letter chip (~28) + gap.
const BADGE_LEFT_DEFAULT_PX = 2;
const CHIP_BADGE_GAP_PX = 4;
const CHIP_RESERVED_OFFSET_PX = 36;

const CitationValueViewerRoot = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interactionActiveRef = useRef(false);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { publish } = useEventBus();
  const { getSourceNumber } = useDocumentFootnotes();
  const patchSecurities = usePatchCapTableSecurities();

  // LIVE cell refs, never the `citationReferences` prop: the review settle
  // paths (handleReviewSettleCitation, accept/dismissFieldOnGrid) reassign
  // cell.citationReferences IN PLACE and this viewer re-renders through its
  // review-store subscription (or a hover), where the prop still holds the
  // pre-settle array captured by the last full datasheet render - keying on it
  // froze a citation-only accept's badge at status=pending until reload
  // (SCAL-9336 re-fix).
  const liveCitationReferences = restProps.cell.citationReferences;

  // When the cell also renders a Pre-FX currency chip (cap-table OIP), the chip
  // is the leftmost element; shift the citation footnote to its right so the
  // two don't overlap (SCAL-9062). The chip reveals on hover OR selection, so
  // pass the same hovered-or-selected signal here to engage the offset exactly
  // when it shows. The hook is called unconditionally at the top level (React
  // hooks rules).
  const chipPresent = useChipPresent(restProps.cell, isHovered || Boolean(restProps.selected));

  // The chip's true width depends on font metrics, the ISO code and the sheet
  // zoom, so the gutter is MEASURED, not guessed: this viewer wraps the chip's
  // subtree, so the rendered chip (marked `data-money-chip` on its shell) is
  // findable under the overlay wrapper. Rect deltas come back in viewport space
  // (scaled by any ancestor zoom transform); dividing by rectWidth/offsetWidth
  // converts them to the local px space `left` positions in. Re-measures on
  // every trigger that can change the chip's footprint - mount/unmount
  // (chipPresent), the entered ISO code, and a datasheet re-parse (cell
  // identity, which zoom and currency changes flow through); the guarded
  // setState settles in one extra pass and never loops.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const enteredCurrency = useEnteredCurrency(restProps.cell.cellAnnotationCellKey);
  const [measuredChipOffset, setMeasuredChipOffset] = useState<number | null>(null);
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const chip = chipPresent && wrapper ? wrapper.querySelector('[data-money-chip]') : null;
    let next: number | null = null;
    if (chip && wrapper) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();
      if (chipRect.width > 0 && wrapperRect.width > 0) {
        const scale = wrapper.offsetWidth > 0 ? wrapperRect.width / wrapper.offsetWidth : 1;
        next = Math.round((chipRect.right - wrapperRect.left) / scale) + CHIP_BADGE_GAP_PX;
      }
    }
    setMeasuredChipOffset(prev => (prev === next ? prev : next));
    // The extra deps are re-measure TRIGGERS (they change the chip's rendered
    // width), not values the measurement reads from React state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipPresent, enteredCurrency, restProps.cell]);
  const citationOffsetLeft = chipPresent ? (measuredChipOffset ?? CHIP_RESERVED_OFFSET_PX) : BADGE_LEFT_DEFAULT_PX;

  // Grid-visible citations (SCAL-8969): a DISMISSED citation never renders a
  // footnote badge - it stays reachable (grayed) from popover/review surfaces only.
  // The cell owns one aggregate badge/popover. Keep every live citation row in
  // that popover (including multiple excerpts from one document), filtering only
  // dismissed rows per the existing grid rule.
  const visibleCitations = useMemo(
    () => (liveCitationReferences ?? []).filter(cit => citationReviewStatus(cit) !== 'dismissed'),
    [liveCitationReferences]
  );

  const citationSourceNumbers = useMemo(
    () =>
      [
        ...new Set(
          visibleCitations
            .map(cit => getSourceNumber(cit.document_reference))
            .filter((sourceNumber): sourceNumber is number => sourceNumber !== undefined)
        ),
      ].sort((a, b) => a - b),
    [visibleCitations, getSourceNumber]
  );
  const citationSourceLabel = citationSourceNumbers.join(',');

  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (!interactionActiveRef.current) {
        setAnchorEl(null);
      }
    }, 120);
  }, [cancelClose]);

  const handleInteractionActiveChange = React.useCallback(
    (active: boolean) => {
      interactionActiveRef.current = active;
      if (active) {
        cancelClose();
      } else {
        scheduleClose();
      }
    },
    [cancelClose, scheduleClose]
  );

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCitationDelete = (citationId: number) => {
    if (activeCitation?.id === citationId) {
      setActiveCitation(undefined);
    }

    patchSecurities(securities => removeCitationFromSecurities(securities, citationId) as typeof securities);
  };

  const citationContainsActive = useMemo(
    () => visibleCitations.some(cit => cit.id === activeCitation?.id),
    [visibleCitations, activeCitation]
  );

  // Dropdown and date-picker cells own the cell interior, so the badge cannot
  // overlay them. Reserve a stable left gutter sized to the badges (so the
  // indicator stays usable and the layout never jumps) instead (SCAL-8987).
  const reservedBadgeWidth = useMemo(() => {
    if (!hasInlineControl(restProps.cell)) {
      return 0;
    }
    // One flexible-width badge plus the 2px left offset and a small margin.
    return Math.max(24, citationSourceLabel.length * 6 + 8) + 6;
  }, [restProps.cell, citationSourceLabel.length]);

  // SCAL-9150 completion: while the review drawer is open, ANY cell whose review item
  // is still PENDING paints its text AI-purple at rest - including citation-only cells
  // (no AI field suggestion), which this viewer alone wraps and which previously read
  // plain. The store item is the SAME source the side panel renders from (the panel
  // flips it on accept/decline), so cell and card can never disagree; the fallback
  // mirrors the aggregator's own derivation - field metadata status when present, the
  // ACTIVE citation's status otherwise - guarded by the same (columnId, alias)
  // eligibility, for the moments before an item lands in the store. Cells with field
  // metadata are also tinted by their outer AIFinding/AIMerge wrapper; both apply the
  // identical scoped-gradient rule, so the overlap is a no-op.
  const drawerOpen = useReviewDrawerOpen();
  // SCAL-9335: only analyst-side users see review styling at all. For everyone
  // else the badges below render normal blue regardless of citation status, and
  // the pending-purple rest state never engages.
  const reviewerMode = useReviewerMode();
  const { columnId, alias } = restProps.cell;
  const storeStatus = useReviewItemStatus(columnId != null ? Number(columnId) : undefined, alias);
  const localStatus =
    restProps.cell.aiFieldMetadata != null
      ? reviewStatusOf(restProps.cell.aiFieldMetadata)
      : citationReviewStatus(pickActiveCitation(restProps.cell.citationReferences));
  const itemStatus = storeStatus ?? (columnId != null && alias ? localStatus : undefined);
  const restIsPurple = reviewerMode && drawerOpen && itemStatus === 'pending';
  const badgeStatus =
    reviewerMode && visibleCitations.some(cit => citationReviewStatus(cit) === 'pending') ? 'pending' : 'accepted';
  const badgeAiCreated = reviewerMode && visibleCitations.some(isAiCreatedCitation);
  // The per-entry AI chip in the popout is reviewer-only too (SCAL-9335 staff
  // tightening): non-reviewers get a plain popout with no AI provenance.
  const citationEntries = visibleCitations.map(cit => ({
    citation: cit,
    aiCreated: reviewerMode && isAiCreatedCitation(cit),
  }));
  const citationCountLabel = `${visibleCitations.length} ${
    visibleCitations.length === 1 ? 'citation' : 'citations'
  } from ${citationSourceNumbers.length === 1 ? 'source' : 'sources'} ${citationSourceLabel}`;

  // SCAL-8969: a DISMISSED field shows NOTHING AI-related on the grid - the citation
  // footnote reverts to a plain cell (the dismissed citation stays reachable only from
  // the review panel). Reuse the field-level review status the grid already keys on
  // (aiFieldMetadata.status); accepted/pending keep their footnote. The same applies
  // per citation: when every citation on the cell is itself dismissed, nothing renders.
  const isDismissed = restProps.cell?.aiFieldMetadata?.status === 'dismissed';

  if (isDismissed || visibleCitations.length === 0) {
    // No citations (or dismissed): render original viewer or plain text
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    // The overlay is the cell's ONE hover region: its isHovered drives the
    // badge reveal AND (via CellHoverContext) the Pre-FX chip deeper in the
    // subtree, so the chip survives the pointer crossing onto the badge - a
    // sibling overlay the chip's own flex row would treat as a mouseleave.
    <StyledCitationValueViewerWrapper
      ref={wrapperRef}
      data-testid="citation-cell-overlay"
      onMouseEnter={() => {
        cancelClose();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        scheduleClose();
        setIsHovered(false);
      }}>
      {(isHovered || Boolean(anchorEl) || citationContainsActive) && (
        <StyledCitationNumberWrapper style={{ left: citationOffsetLeft }}>
          <StyledCitationNumber
            data-testid="citation-badge"
            data-review-status={badgeStatus}
            data-ai-created={badgeAiCreated || undefined}
            aria-label={citationCountLabel}
            onMouseDown={e => e.stopPropagation()}
            onClick={handleBadgeClick}
            sx={citationBadgeSx({
              pending: badgeStatus === 'pending',
              aiCreated: badgeAiCreated,
              active: Boolean(anchorEl) || citationContainsActive,
            })}>
            {citationSourceLabel}
          </StyledCitationNumber>
        </StyledCitationNumberWrapper>
      )}

      <StyledCitationValueViewer
        reservedBadgeWidth={reservedBadgeWidth}
        data-review-purple={restIsPurple || undefined}
        sx={restIsPurple ? reviewPendingTextSx : undefined}>
        <CellHoverContext.Provider value={isHovered}>
          {OriginalValueViewer ? (
            <OriginalValueViewer value={value} {...restProps} />
          ) : (
            <FastValueViewer className="text-cell" value={value} {...restProps} />
          )}
        </CellHoverContext.Provider>
      </StyledCitationValueViewer>

      {anchorEl && (
        <CitationPopover
          onView={cit => {
            // Highlight color follows the CITATION'S review status (SCAL-8986), not
            // the navigation source: a still-pending citation traced from the cell
            // footnote reads AI-purple, an accepted/manual one plain yellow. For
            // non-reviewers (SCAL-9335) the trace is always the plain variant.
            setActiveCitation(cit, statusToHighlightVariant(reviewerMode ? citationReviewStatus(cit) : 'accepted'));
            publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
              documentReferenceId: cit.document_reference,
              expandDrawer: true,
              activeCitationData: cit,
            });
          }}
          open
          anchorEl={anchorEl}
          onClose={scheduleClose}
          entries={citationEntries}
          columnarIdentifier={titleCell}
          onDelete={handleCitationDelete}
          onInteractionActiveChange={handleInteractionActiveChange}
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
          }}
        />
      )}
    </StyledCitationValueViewerWrapper>
  );
};

const CitationValueViewer = (props: CitationValueViewerProps) => {
  const isNested = React.useContext(CitationNestingContext);

  if (isNested) {
    const {
      value,
      originalValueViewer: OriginalValueViewer,
      citationReferences: _citationReferences,
      citationNumber: _citationNumber,
      citation: _citation,
      ...restProps
    } = props;
    return OriginalValueViewer ? (
      <OriginalValueViewer value={value} {...restProps} />
    ) : (
      <FastValueViewer value={value} {...restProps} />
    );
  }

  return (
    <CitationNestingContext.Provider value>
      <CitationValueViewerRoot {...props} />
    </CitationNestingContext.Provider>
  );
};

export default CitationValueViewer;
